"use client";

import { useTranslation } from "@/app/i18n/client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "../../../ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../../../ui/button";
import * as actions from "@/actions";
import { useState, useEffect } from "react";
import {
    SignUpMobileValidatorSchema,
    SignUpValidatorSchema,
    signupEmailValidatorSchema,
    signupMobileValidatorSchema,
} from "@/lib/validators/signup";
import { CheckCircle2 } from "lucide-react";
import Link from "next/link";
import { toast } from "react-hot-toast";
import OTPFormSignup from "../otp-form-signup";
import { sendOTPSignup } from "@/actions";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { Loader2 } from "lucide-react";
import { useContext } from "react";
import LocaleLink from "@/components/locale-link";
import DividerHorizontal from "@/components/divider-horizontal";
import { CallAPI_UrlAuthFB, CallAPI_UrlAuthGoogle } from "@/util/services/auth/api";
import { useRouter, useSearchParams } from "next/navigation";
import { SessionStorageKeys } from "@/constants/constants";

interface SignUpFormProps {
    guestId?: string;
    sso?: string;
    callbackUrl?: string;
    config?: any;
    radiusStyle: any;
    consentDetail?: any;
    isUseConsent?: any;

    // Refer-friend (Sprint A): optional referral context
    referralCode?: string;
    referrerName?: string;
    isReferralEnabled?: boolean;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
    guestId,
    sso,
    callbackUrl,
    config,
    radiusStyle,
    consentDetail,
    isUseConsent,
    referralCode,
    isReferralEnabled,
}) => {
    const systemConfig = config;
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isRegistered, setIsRegistered] = useState<boolean>(false);
    const [isEmail, setEmail] = useState<string>("");
    const [isPhone, setPhone] = useState<string>("");
    const [isPassword, setPassword] = useState<string>("");
    const [isOTP, setIsOTP] = useState<boolean>(false);
    const [isRefCode, setIsRefCode] = useState<string>("");
    const [isRef, setIsRef] = useState<string>("");
    const [isConsent, setIsConsent] = useState<boolean>(
        isUseConsent && consentDetail && consentDetail.body_html ? false : true,
    );
    const [signUpMethod, setSignUpMethod] = useState<"email" | "phone">(
        "email"
    );
    const [urlAuthGoogle, setUrlAuthGoogle] = useState<string>("");
    const [urlAuthFB, setUrlAuthFB] = useState<string>("");
    const colorStyle = useContext(ColorStyleContext);
    const router = useRouter();
    const searchParams = useSearchParams();

    // Remember the referral code (when on the referral page) and, when the user later opens the
    // plain /signup page on their own, send them back to the remembered referral page so the
    // referral context is not lost.
    useEffect(() => {
        if (typeof window === "undefined") return;

        if (referralCode) {
            // On the referral page: persist the (already server-validated) code.
            sessionStorage.setItem(SessionStorageKeys.referralCode, referralCode);
            return;
        }

        // On the plain /signup page: skip the redirect while an SSO / callback / guest flow is in
        // progress so those query params are preserved.
        const hasSpecialFlow =
            searchParams.get("sso") || searchParams.get("callbackUrl") || searchParams.get("id");
        if (hasSpecialFlow) return;

        const stored = sessionStorage.getItem(SessionStorageKeys.referralCode);
        if (stored) {
            if (!isReferralEnabled) {
                // Referral feature is disabled: clear the stored code to avoid redirect loop.
                sessionStorage.removeItem(SessionStorageKeys.referralCode);
                return;
            }
            router.replace(`/signup/referral/${encodeURIComponent(stored)}`);
        }
    }, [referralCode, router, searchParams, isReferralEnabled]);

    useEffect(() => {
        const fetchUrl = async () => {
            const urlAuthFB = await CallAPI_UrlAuthFB(referralCode);
            setUrlAuthFB(urlAuthFB.result);
            const urlAuthGoogle = await CallAPI_UrlAuthGoogle(referralCode);
            setUrlAuthGoogle(urlAuthGoogle.result);
        };
        fetchUrl();
    }, [referralCode]);

    const form = useForm<SignUpValidatorSchema>({
        resolver: zodResolver(signupEmailValidatorSchema),
        mode: "onChange",
    });

    const mobileForm = useForm<SignUpMobileValidatorSchema>({
        resolver: zodResolver(signupMobileValidatorSchema),
        mode: "onChange",
    });

    const onSubmitMobileRegister = async (values: SignUpMobileValidatorSchema) => {
        setLoading(true);
        const phoneNumber = values.phone_number;

        // Validate Thai mobile format before registering
        // Allowed: 010xxxxxxx, 06xxxxxxxx to 09xxxxxxxx
        if (!/^0(10|[6-9]\d)\d{7}$/.test(phoneNumber)) {
            toast.error(`${t("login.phone_number_invalid")}`, {
                position: "bottom-center",
                duration: 3000,
                style: { zIndex: 99999 },
            });
            setLoading(false);
            return;
        }

        return actions
            .signUpPhone(phoneNumber, referralCode, guestId)
            .then(async (res) => {
                if (res.success && res.result.success) {
                    form.reset();
                    // Referral consumed: stop bouncing later /signup visits back to the referral page.
                    sessionStorage.removeItem(SessionStorageKeys.referralCode);
                    setIsRegistered(true);
                    setPhone(phoneNumber);
                    setIsRefCode(res.result.refCode);
                    setIsRef(res.result.ref);
                } else {
                    const errorKey = `signup.${res.error?.[0] || "signup.signup_error"}`;
                    
                    if (errorKey === "signup.USER_PHONE_NUMBER_ALREADY_EXISTS" || res.error?.[0] === "USER_PHONE_NUMBER_ALREADY_EXISTS") {
                        // Immediately inform user their phone already exists, then attempt OTP recovery flow
                        toast.error(`${t(errorKey as any)}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });

                        const response = await sendOTPSignup(phoneNumber);
                        if (response.result.success) {
                            setIsRefCode(response.result.refCode);
                            setIsRef(response.result.ref);
                            setPhone(phoneNumber);
                            setIsRegistered(true);
                        }
                        if (response.result.skip && !response.result.success) {
                            toast.error(`${t(errorKey as any)}`, {
                                position: "bottom-center",
                                duration: 3000,
                            });
                        }
                    } else {
                        // Any other error -> generic toast
                        toast.error(`${t(errorKey as any)}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });
                    }
                }
            })
            .catch((error: any) => {
                toast.error(`${t("signup.signup_error")}`, {
                    position: "bottom-center",
                    duration: 3000,
                });
            })
            .finally(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 3000);
            });
    };

    const onSubmit = async (values: SignUpValidatorSchema) => {
        setLoading(true);
        const email = (values.email ?? "").toLowerCase();
        const password = values.password ?? "";
        const verifyEmail = systemConfig?.enableVerifyEmail || false;

        return actions
            .signUpEmail(email, password, referralCode, guestId)
            .then(async (res) => {
                if (res.success) {
                    form.reset();
                    // Referral consumed: stop bouncing later /signup visits back to the referral page.
                    sessionStorage.removeItem(SessionStorageKeys.referralCode);
                    if (!verifyEmail) {
                        actions
                            .signInWithCredentials({
                                email,
                                password,
                                authFlow: "email",
                                callbackUrl: callbackUrl,
                            })
                            .catch((error: any) => {
                                toast.error(`${t("login.toast_failed_message")}`, {
                                    position: "bottom-center",
                                    duration: 3000,
                                });
                            })
                            .finally(() => {
                                setTimeout(() => {
                                    setLoading(false);
                                }, 3000);
                            });
                        return;
                    }
                    setIsRegistered(true);
                    setEmail(res.result.email);
                    setPassword(password);
                    setIsRefCode(res.result.refCode);
                    setIsRef(res.result.ref);
                } else {
                    const errorKey = `signup.${res.error?.[0] || "signup.signup_error"}`;
                    if (errorKey === "signup.USER_ALREADY_EXISTS") {
                        if (verifyEmail) {
                            const response = await sendOTPSignup(email);
                            if (response.result.success) {
                                setIsRefCode(response.result.refCode);
                                setIsRef(response.result.ref);
                                setEmail(email);
                                setPassword(password);
                                setIsRegistered(true);
                            }
                            if (response.result.skip && !response.result.success) {
                                toast.error(`${t(errorKey as any)}`, {
                                    position: "bottom-center",
                                    duration: 3000,
                                });
                            }
                        } else {
                            toast.error(`${t(errorKey as any)}`, {
                                position: "bottom-center",
                                duration: 3000,
                            });
                        }
                    }
                }
            })
            .catch((error: any) => {
                toast.error(`${t("signup.signup_error")}`, {
                    position: "bottom-center",
                    duration: 3000,
                });
            })
            .finally(() => {
                setTimeout(() => {
                    setLoading(false);
                }, 3000);
            });
    };

    const onOTPSubmit = async (values: any) => {
        if (signUpMethod === "email") {
            if (values) {
                setIsOTP(true);

                actions
                    .signInWithCredentials({
                        email: isEmail,
                        password: isPassword,
                        authFlow: "email",
                        callbackUrl: callbackUrl,
                    })
                    .catch((error: any) => {
                        toast.error(`${t("login.toast_failed_message")}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setLoading(false);
                        }, 3000);
                    });
            }
        } else if (signUpMethod === "phone") {
            if (values) {
                setIsOTP(true);

                actions
                    .signInWithCredentials({
                        phoneNumber: isPhone,
                        authFlow: "phone",
                        callbackUrl: callbackUrl,
                    })
                    .catch((error: any) => {
                        toast.error(`${t("login.toast_failed_message")}`, {
                            position: "bottom-center",
                            duration: 3000,
                        });
                    })
                    .finally(() => {
                        setTimeout(() => {
                            setLoading(false);
                        }, 3000);
                    });
            }
        }
    };

    const boostrap4 = `
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap-utilities.min.css">
    `;

    const handleInputConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setIsConsent(checked);
        // Trigger form validation to update button state
        if (signUpMethod === "phone") {
            mobileForm.trigger();
        } else {
            form.trigger();
        }
    };

    return isRegistered ? (
        <div className="flex flex-col items-center p-5 gap-2">
            {isOTP ? (
                <>
                    <CheckCircle2 size={80} strokeWidth={1} className="text-green-500" />
                    <span className="font-normal text-2xl text-black">{t("signup.signup_done")}</span>
                    <Link href={callbackUrl ? `/signin?callbackUrl=${encodeURIComponent(callbackUrl)}` : "/signin"} className="font-normal text-sm text-c2">
                        {t("signup.signup_back_to_login")}
                    </Link>
                </>
            ) : (
                <>
                    {signUpMethod === "email" ? (
                        <OTPFormSignup
                            numDigits={6}
                            type="email"
                            value={isEmail}
                            data={{
                                ref: isRef,
                                transId: isRef,
                                refCode: isRefCode,
                            }}
                            onOTPSubmit={(isSuccess) => {
                                onOTPSubmit(isSuccess);
                            }}
                            radiusStyle={radiusStyle}
                        />
                    ) : (
                        <OTPFormSignup
                            numDigits={6}
                            type="mobile"
                            value={isPhone}
                            data={{
                                ref: isRef,
                                transId: isRef,
                                refCode: isRefCode,
                            }}
                            onOTPSubmit={(isSuccess) => {
                                onOTPSubmit(isSuccess);
                            }}
                            radiusStyle={radiusStyle}
                        />
                    )}
                </>
            )}
        </div>
    ) : (
        <div className="mt-4">
            {/* Only show tabs if both methods are available */}
            {systemConfig?.enablePhoneOTP !== false && systemConfig?.enablePhoneOTP !== "email" && (
                <div className="flex w-full justify-center gap-4 border-b">
                    <button
                        className={`px-4 py-2 font-medium ${
                            signUpMethod === "email" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"
                        }`}
                        onClick={() => setSignUpMethod("email")}>
                        {t("signup.signup_tab_email")}
                    </button>

                    <button
                        className={`px-4 py-2 font-medium ${
                            signUpMethod === "phone" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500"
                        }`}
                        onClick={() => setSignUpMethod("phone")}>
                        {t("signup.signup_tab_phone")}
                    </button>
                </div>
            )}
            {/* Show email form if method is email OR if only email is enabled */}
            {signUpMethod === "email" || systemConfig?.enablePhoneOTP === "email" ? (
                <Form key="email" {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="flex flex-col space-y-4 p-6 min-h-[384px] justify-between">
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field, formState }) => (
                                <FormItem>
                                    <FormLabel
                                        className="form-input-label"
                                        style={{
                                            color: colorStyle?.text || "#3d3d3d",
                                        }}>
                                        {t("signup.signup_input_email")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder={t("signup.signup_input_email")}
                                            {...field}
                                            value={field.value || ""}
                                            className={cn("form-input", formState.errors.email && "form-input-error")}
                                        />
                                    </FormControl>
                                    <FormMessage translate="yes" />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field, formState }) => (
                                <FormItem>
                                    <FormLabel
                                        className="form-input-label"
                                        style={{
                                            color: colorStyle?.text || "#3d3d3d",
                                        }}>
                                        {t("signup.signup_input_password")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="password"
                                            placeholder={t("signup.signup_input_password")}
                                            {...field}
                                            value={field.value || ""}
                                            className={cn(
                                                "form-input",
                                                formState.errors.password && "form-input-error",
                                            )}
                                        />
                                    </FormControl>
                                    <FormMessage translate="yes" />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field, formState }) => (
                                <FormItem>
                                    <FormLabel
                                        className="form-input-label"
                                        style={{
                                            color: colorStyle?.text || "#3d3d3d",
                                        }}>
                                        {t("signup.signup_input_confirm_password")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="password"
                                            placeholder={t("signup.signup_input_confirm_password")}
                                            {...field}
                                            value={field.value || ""}
                                            className={cn(
                                                "form-input",
                                                formState.errors.confirmPassword && "form-input-error",
                                            )}
                                        />
                                    </FormControl>
                                    <FormMessage translate="yes" />
                                </FormItem>
                            )}
                        />
                        {isUseConsent && consentDetail && consentDetail.body_html && (
                            <div className="w-full flex flex-row gap-4">
                                <div
                                    id="consent-checkbox-container"
                                    key="consent-checkbox"
                                    className="flex items-start space-x-2"
                                    style={{ color: colorStyle.text || "#3d3d3d" }}>
                                    <input
                                        type="checkbox"
                                        id="consent-checkbox"
                                        name="consent-checkbox"
                                        value={isConsent ? "true" : "false"}
                                        onChange={handleInputConsentChange}
                                        className="relative top-[3px]"
                                        disabled={isLoading}
                                    />
                                    <label id="consent-checkbox-label" htmlFor="consent-checkbox" className="text-sm">
                                        <div
                                            id="consent-checkbox-label-content"
                                            className="relative w-full overflow-hidden"
                                            style={{
                                                color: colorStyle?.text || "#3d3d3d",
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: `${boostrap4} ${consentDetail.body_html}` || "",
                                            }}></div>
                                    </label>
                                </div>
                            </div>
                        )}
                        <div className="w-full flex flex-col items-center">
                            <button
                                className={`w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color flex items-center justify-center text-lg font-normal  ${
                                    !isConsent ? "cursor-not-allowed opacity-50" : ""
                                } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                                type="submit"
                                disabled={isLoading || !isConsent}
                                aria-disabled={isLoading || !isConsent}
                                style={
                                    {
                                        "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                        "--dynamic-hover-bg":
                                            !isConsent || isLoading
                                                ? colorStyle?.buttonBgNormal || "#009eca"
                                                : colorStyle?.buttonBgHover || "#254b7c",
                                        "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                        "--dynamic-hover-color":
                                            !isConsent || isLoading
                                                ? colorStyle?.buttonTextNormal || "#ffffff"
                                                : colorStyle?.buttonTextHover || "#ffffff",
                                        "--dynamic-focus-color":
                                            !isConsent || isLoading
                                                ? colorStyle?.buttonTextNormal || "#ffffff"
                                                : colorStyle?.buttonTextHover || "#ffffff",
                                        borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                        borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                        borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                        borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                    } as React.CSSProperties
                                }>
                                <div className="flex items-center justify-center gap-2">
                                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    {t("signup.signup_btn_signup")}
                                </div>
                            </button>
                        </div>
                    </form>
                </Form>
            ) : systemConfig?.enablePhoneOTP === false || systemConfig?.enablePhoneOTP === "email" ? null : (
                <Form key="mobile" {...mobileForm}>
                    <form
                        onSubmit={mobileForm.handleSubmit(onSubmitMobileRegister)}
                        className="flex flex-col space-y-4 p-6 min-h-[384px] justify-between">
                        <div className="flex flex-col gap-4">
                            <FormField
                                control={mobileForm.control}
                                name="phone_number"
                                render={({ field, formState }) => (
                                    <FormItem>
                                        <FormLabel
                                            className="form-input-label"
                                            style={{
                                                color: colorStyle?.text || "#3d3d3d",
                                            }}>
                                            {t("signup.signup_input_phone")}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t("signup.signup_input_phone")}
                                                {...field}
                                                value={field.value || ""}
                                                className={cn(
                                                    "form-input",
                                                    formState.errors.phone_number && "form-input-error",
                                                )}
                                            />
                                        </FormControl>
                                        <FormMessage translate="yes" />
                                    </FormItem>
                                )}
                            />
                            {isUseConsent && consentDetail && consentDetail.body_html && (
                                <div className="w-full flex flex-row gap-4">
                                    <div
                                        id="consent-checkbox-container"
                                        key="consent-checkbox"
                                        className="flex items-start space-x-2"
                                        style={{ color: colorStyle.text || "#3d3d3d" }}>
                                        <input
                                            type="checkbox"
                                            id="consent-checkbox"
                                            name="consent-checkbox"
                                            value={isConsent ? "true" : "false"}
                                            onChange={handleInputConsentChange}
                                            className="relative top-[3px]"
                                            disabled={isLoading}
                                        />
                                        <label
                                            id="consent-checkbox-label"
                                            htmlFor="consent-checkbox"
                                            className="text-sm">
                                            <div
                                                id="consent-checkbox-label-content"
                                                className="relative w-full overflow-hidden"
                                                style={{
                                                    color: colorStyle?.text || "#3d3d3d",
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: `${boostrap4} ${consentDetail.body_html}` || "",
                                                }}></div>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="w-full flex flex-col items-center">
                            <button
                                className={`w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color flex items-center justify-center text-lg font-normal  ${
                                    !isConsent || !mobileForm.formState.isValid ? "cursor-not-allowed opacity-50" : ""
                                } ${isLoading ? "cursor-not-allowed opacity-50" : ""}`}
                                type="submit"
                                aria-disabled={isLoading || !isConsent || !mobileForm.formState.isValid}
                                disabled={isLoading || !isConsent || !mobileForm.formState.isValid}
                                style={
                                    {
                                        "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                        "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                        "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                        "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                        "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                        borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                        borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                        borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                        borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                    } as React.CSSProperties
                                }>
                                <div className="flex items-center justify-center gap-2">
                                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    {t("signup.signup_btn_signup")}
                                </div>
                            </button>
                        </div>
                    </form>
                </Form>
            )}

            {(systemConfig?.enableFacebookAuth || systemConfig?.enableGoogleAuth) && (
                <div className="flex flex-col px-6 pb-6">
                    <div className="flex flex-row gap-4">
                        <DividerHorizontal />
                        <h4
                            className="grow font-normal text-sm w-fit text-center content-center whitespace-nowrap"
                            style={{
                                color: colorStyle?.text || "#3d3d3d",
                            }}>
                            {t("login.or_login_with")}
                        </h4>
                        <DividerHorizontal />
                    </div>

                    <div className="flex flex-col sm:flex-col px-6 gap-5">
                        {systemConfig?.enableGoogleAuth && (
                            <a className="w-full" href={urlAuthGoogle}>
                                <button
                                    type="submit"
                                    className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                    style={
                                        {
                                            "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                            borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                            borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                            borderBottomLeftRadius:
                                                radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                            borderBottomRightRadius:
                                                radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                        } as React.CSSProperties
                                    }>
                                    <img
                                        src="/assets/icons/google-login.svg"
                                        alt="Google"
                                        className="h-[18px] w-[18px]"
                                    />
                                    {t("login.sign_in_with_google")}
                                </button>
                            </a>
                        )}
                        {systemConfig?.enableFacebookAuth && (
                            <a className="w-full" href={urlAuthFB}>
                                <button
                                    type="submit"
                                    className="inline-flex h-10 w-full dynamic-bg dynamic-color dynamic-border flex items-center justify-center text-lg font-bold gap-2 border dynamic-border"
                                    style={
                                        {
                                            "--dynamic-border": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-border": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-color": colorStyle?.buttonBgNormal || "#009eca",
                                            "--dynamic-hover-color": colorStyle?.buttonBgHover || "#254b7c",
                                            "--dynamic-focus-color": colorStyle?.buttonBgHover || "#254b7c",
                                            borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                            borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                            borderBottomLeftRadius:
                                                radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                            borderBottomRightRadius:
                                                radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                        } as React.CSSProperties
                                    }>
                                    <img
                                        src="/assets/icons/facebook-login.svg"
                                        alt="Facebook"
                                        className="h-[18px] w-[18px]"
                                    />
                                    {t("login.sign_in_with_facebook")}
                                </button>
                            </a>
                        )}
                    </div>
                </div>
            )}

            <div className="w-full flex items-center justify-center mb-6">
                <h4 className="font-normal text-lg w-fit text-center dynamic-color content-center whitespace-nowrap" style={{
                    color : colorStyle?.text || "#3d3d3d"}}>
                    <span className="text-gray-500">{t("login.login_already_member")}</span>&nbsp;
                    <LocaleLink href={callbackUrl ? `/signin?callbackUrl=${encodeURIComponent(callbackUrl)}` : "/signin"} className="dynamic-color dynamic-hover-color font-bold" style={{
                        "--dynamic-color": colorStyle?.link || "#009eca",
                        "--dynamic-hover-color": colorStyle?.linkHover || "#254b7c",
                    } as React.CSSProperties}>
                        {t("login.login_btn_login_here")}
                    </LocaleLink>
                </h4>
            </div>
        </div>
    );
};

export default SignUpForm;
